import React, { useEffect, useMemo, useState } from "react";
import { MonthlyCollectionChart } from "../../components/dashboard/MonthlyCollectionChart";   // Phase 213
import { Building2, Layers, IndianRupee, Home, FileDown, Database, Wallet, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { KpiCard, PageHeader, Spinner, StatusBadge } from "../../components/common";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Button } from "../../components/ui/button";
import { api, fmtINR, downloadBlob } from "../../lib/api";
import { sourceLabel } from "../admin/AdminDashboard";
import { TwoStepBanner } from "../../components/TwoStepBanner";

const COLORS = ["hsl(206,88%,28%)", "hsl(152,55%,32%)", "hsl(38,92%,45%)", "hsl(199,89%,40%)", "hsl(0,72%,50%)", "hsl(260,50%,50%)", "hsl(180,50%,35%)"];

export default function SuperDashboard() {
  const [data, setData] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [revenue, setRevenue] = useState(null);
  const [selMuni, setSelMuni] = useState("");
  const [gateway, setGateway] = useState(null); // Phase 123: stored Twilio balance snapshot (no Twilio call)

  useEffect(() => {
    api.get("/platform/notification-providers/status").then((r) => setGateway(r.data)).catch(() => {});
    api.get("/platform/dashboard").then((r) => setData(r.data)).catch(() => {});
    api.get("/platform/revenue-breakdown").then((r) => {
      setRevenue(r.data);
      const withRev = (r.data.municipalities || []).filter((m) => m.total_collected > 0);
      const first = withRev[0] || (r.data.municipalities || [])[0];
      if (first) setSelMuni(first.code);
    }).catch(() => {});
  }, []);

  const muniRev = useMemo(
    () => (revenue?.municipalities || []).find((m) => m.code === selMuni),
    [revenue, selMuni]
  );

  const downloadOverview = async () => {
    setDownloading(true);
    try {
      await downloadBlob("/platform/overview-pdf", "MunicipalConnect-Project-Overview.pdf");
      toast.success("Project overview PDF downloaded");
    } catch {
      toast.error("Could not download the overview PDF");
    } finally {
      setDownloading(false);
    }
  };

  if (!data) return <Spinner />;

  return (
    <div>
      <PageHeader title="Platform Overview" subtitle="Cross-municipality analytics and health">
        <Button variant="outline" onClick={downloadOverview} disabled={downloading} data-testid="download-overview-pdf-button">
          <FileDown className="h-4 w-4 mr-2" /> {downloading ? "Preparing..." : "Project Overview (PDF)"}
        </Button>
      </PageHeader>
      <TwoStepBanner />
      {gateway?.low && !gateway?.simulation_mode && (
        <Card className="border-destructive/40 bg-destructive/5 mb-6" data-testid="dashboard-low-balance-banner">
          <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <Wallet className="h-5 w-5 text-destructive" />
              <div>
                <p className="text-sm font-semibold">SMS gateway balance is low: {Number(gateway.balance).toFixed(2)} {gateway.currency}</p>
                <p className="text-xs text-muted-foreground">Below the {Number(gateway.threshold).toFixed(2)} {gateway.currency} alert threshold — top up Twilio so citizen SMS keep flowing.</p>
              </div>
            </div>
            <Button asChild variant="outline" size="sm" data-testid="dashboard-low-balance-link">
              <Link to="/super/notifications">Notifications settings <ArrowRight className="h-3.5 w-3.5 ml-1.5" /></Link>
            </Button>
          </CardContent>
        </Card>
      )}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KpiCard label="Municipalities" value={data.municipalities} sub={`${data.active_municipalities} active`} icon={Building2} testId="kpi-municipalities" />
        <KpiCard label="Service Templates" value={data.master_services} sub={`${data.service_assignments} assignments`} icon={Layers} testId="kpi-services" />
        <KpiCard label="Total Properties" value={data.total_properties} icon={Home} testId="kpi-properties" />
        <KpiCard label="Total Collected" value={data.total_collected} money icon={IndianRupee} testId="kpi-collected" />
      </div>
      <Card className="shadow-sm mb-6">
        <CardHeader className="pb-2"><CardTitle className="text-base font-heading">Municipality performance</CardTitle></CardHeader>
        <CardContent>
          <Table data-testid="muni-stats-table">
            <TableHeader>
              <TableRow>
                <TableHead>Municipality</TableHead>
                <TableHead>State</TableHead>
                <TableHead className="text-right">Properties</TableHead>
                <TableHead className="text-right">Demand</TableHead>
                <TableHead className="text-right">Collected</TableHead>
                <TableHead>Server</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.municipality_stats.map((m) => (
                <TableRow key={m.code} className="hover:bg-muted/60">
                  <TableCell className="font-medium">{m.name}</TableCell>
                  <TableCell>{m.state || "-"}</TableCell>
                  <TableCell className="text-right tabular-nums">{m.properties ?? "-"}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtINR(m.total_demand)}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtINR(m.collected)}</TableCell>
                  <TableCell><StatusBadge status={m.server_status} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Phase 213: Monthly Collection live graph for the selected municipality (same graph the Municipal Admin sees) */}
      <Card className="shadow-sm mb-6" data-testid="super-monthly-collection-wrapper">
        <CardHeader className="pb-0">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Building2 className="h-4 w-4 text-muted-foreground" /> Municipality collection graph
            </CardTitle>
            <Select value={selMuni} onValueChange={setSelMuni}>
              <SelectTrigger className="w-64" data-testid="monthly-collection-muni-select">
                <SelectValue placeholder="Select municipality" />
              </SelectTrigger>
              <SelectContent>
                {(revenue?.municipalities || []).map((m) => (
                  <SelectItem key={m.code} value={m.code} data-testid={`monthly-collection-muni-option-${m.code}`}>
                    {m.name} ({m.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="pt-3">
          {selMuni ? (
            <MonthlyCollectionChart key={selMuni} code={selMuni} className="border-0 shadow-none" testIdPrefix="super-monthly-collection" compactHeader
              canEditTargets municipalityName={muniRev?.name ? `${muniRev.name} (${selMuni.toUpperCase()})` : ""} />
          ) : (
            <p className="text-sm text-muted-foreground text-center py-10" data-testid="super-monthly-collection-no-muni">Select a municipality to see its month-wise collection</p>
          )}
        </CardContent>
      </Card>

      <Card className="shadow-sm" data-testid="platform-revenue-card">
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="text-base font-heading flex items-center gap-2">
              <Database className="h-4 w-4 text-muted-foreground" /> Revenue by data source
            </CardTitle>
            <Select value={selMuni} onValueChange={setSelMuni}>
              <SelectTrigger className="w-56" data-testid="revenue-muni-select">
                <SelectValue placeholder="Select municipality" />
              </SelectTrigger>
              <SelectContent>
                {(revenue?.municipalities || []).map((m) => (
                  <SelectItem key={m.code} value={m.code} data-testid={`revenue-muni-option-${m.code}`}>
                    {m.name} · {fmtINR(m.total_collected)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {!revenue ? (
            <p className="text-sm text-muted-foreground text-center py-10">Loading revenue breakdown…</p>
          ) : !muniRev || muniRev.by_source.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-10" data-testid="revenue-empty-state">
              No demand or payment data for this municipality yet
            </p>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-1 h-64" data-testid="platform-revenue-source-chart">
                <p className="text-xs font-medium text-muted-foreground mb-1">Demand vs collected by source</p>
                <ResponsiveContainer width="100%" height="92%">
                  <BarChart data={muniRev.by_source.map((s) => ({ ...s, label: sourceLabel(s.source) }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e9ef" />
                    <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} angle={-14} textAnchor="end" height={46} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip formatter={(v) => fmtINR(v)} />
                    <Bar dataKey="demand" name="Demand" fill="hsl(206,30%,75%)" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="collected" name="Collected" fill="hsl(152,55%,32%)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="lg:col-span-1 h-64" data-testid="platform-revenue-mode-chart">
                <p className="text-xs font-medium text-muted-foreground mb-1">Payment mode split</p>
                {muniRev.by_mode.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center pt-16">No payments recorded yet</p>
                ) : (
                  <ResponsiveContainer width="100%" height="92%">
                    <PieChart>
                      <Pie data={muniRev.by_mode} dataKey="amount" nameKey="mode" innerRadius={45} outerRadius={75} paddingAngle={2}>
                        {muniRev.by_mode.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => fmtINR(v)} />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                )}
              </div>
              <div className="lg:col-span-1 overflow-x-auto">
                <p className="text-xs font-medium text-muted-foreground mb-1">Source totals</p>
                <Table data-testid="platform-revenue-source-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Source</TableHead>
                      <TableHead className="text-right">Collected</TableHead>
                      <TableHead className="text-right">Share</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {muniRev.by_source.map((s) => (
                      <TableRow key={s.source} data-testid={`platform-revenue-row-${s.source}`}>
                        <TableCell className="font-medium">{sourceLabel(s.source)}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtINR(s.collected)}</TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">
                          {muniRev.total_collected > 0 ? `${Math.round((s.collected / muniRev.total_collected) * 100)}%` : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="border-t-2">
                      <TableCell className="font-semibold">Total</TableCell>
                      <TableCell className="text-right tabular-nums font-semibold" data-testid="platform-revenue-total">
                        {fmtINR(muniRev.total_collected)}
                      </TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
